import { useRef, useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Badge } from "@/components/ui/badge";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import {
  Send,
  Bot,
  User,
  Wrench,
  Loader2,
  Sparkles,
  ChevronRight,
  AlertTriangle,
  WifiOff,
  RefreshCw,
  Square,
} from "lucide-react";
import { Skeleton } from "@/components/ui/skeleton";
import ReactMarkdown from "react-markdown";
import { useOnlineStatus } from "@/hooks/use-online-status";
import type { AssistantMessage, FreelancerSugerido, SugestaoAtiva } from "./useVagaAssistant";
import { PaginatedFreelancerCards } from "./PaginatedFreelancerCards";

interface AssistantChatProps {
  messages: AssistantMessage[];
  isLoading: boolean;
  onSendMessage: (message: string) => void;
  /** Botão "Tentar novamente" quando uma mensagem fica em status `error` ou `partial`. */
  onRetry?: () => void;
  /** Aborta o stream atual quando user clica "Parar resposta". */
  onCancel?: () => void;
  suggestions?: string[];
  sugestoesAtivas?: SugestaoAtiva[];
  loadingSuggestions?: boolean;
  /** Ações de kanban aplicadas aos cards de freelancer renderizados inline. */
  onSelecionados?: (freelancerId: string) => void;
  onShortlist?: (freelancerId: string) => void;
  onDescartar?: (freelancer: FreelancerSugerido) => void;
  isProcessing?: boolean;
}

export function AssistantChat({
  messages,
  isLoading,
  onSendMessage,
  onRetry,
  onCancel,
  suggestions = [],
  sugestoesAtivas = [],
  loadingSuggestions = false,
  onSelecionados,
  onShortlist,
  onDescartar,
  isProcessing,
}: AssistantChatProps) {
  const [input, setInput] = useState("");
  const scrollRef = useRef<HTMLDivElement>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const isOnline = useOnlineStatus();

  // Auto-scroll para última mensagem
  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages]);

  const handleSubmit = () => {
    if (!input.trim() || isLoading) return;
    onSendMessage(input.trim());
    setInput("");
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSubmit();
    }
  };

  return (
    <div className="flex flex-col h-full">
      {/* Mensagens */}
      <ScrollArea className="flex-1 px-3" ref={scrollRef}>
        <div className="space-y-3 py-3">
          {messages.length === 0 && (
            <div className="text-center py-8 text-muted-foreground text-sm">
              <Bot className="h-8 w-8 mx-auto mb-2 opacity-50" />
              <p>Olá! Sou o assistente de seleção.</p>
              <p className="text-xs mt-1">
                Me diga o que você precisa para esta vaga e vou buscar os melhores freelancers.
              </p>

              {loadingSuggestions && (
                <div className="mt-4 space-y-2 px-2">
                  <Skeleton className="h-8 w-full rounded-full" />
                  <Skeleton className="h-8 w-4/5 mx-auto rounded-full" />
                  <Skeleton className="h-8 w-full rounded-full" />
                </div>
              )}

              {!loadingSuggestions && suggestions.length > 0 && (
                <div className="mt-4 flex flex-col gap-2 px-2">
                  {suggestions.map((suggestion, i) => (
                    <button
                      key={i}
                      onClick={() => onSendMessage(suggestion)}
                      disabled={isLoading}
                      className="text-left text-xs px-3 py-2 rounded-full border border-purple-200 bg-purple-50 hover:bg-purple-100 text-purple-700 transition-colors flex items-center gap-1.5 disabled:opacity-50"
                    >
                      <Sparkles className="h-3 w-3 shrink-0" />
                      <span>{suggestion}</span>
                    </button>
                  ))}
                </div>
              )}
            </div>
          )}

          {messages.map((msg) => (
            <MessageBubble
              key={msg.id}
              message={msg}
              onRetry={onRetry}
              onSelecionados={onSelecionados}
              onShortlist={onShortlist}
              onDescartar={onDescartar}
              isProcessing={isProcessing}
            />
          ))}

          {/* Sugestões inteligentes como botões */}
          {!isLoading && sugestoesAtivas.length > 0 && messages.length > 0 && (
            <div className="flex flex-col gap-1.5 mt-2 mb-1">
              <span className="text-[11px] text-muted-foreground font-medium px-1">Sugestões:</span>
              {sugestoesAtivas.map((sugestao, i) => (
                <button
                  key={i}
                  onClick={() => onSendMessage(sugestao.prompt)}
                  className="text-left text-xs px-3 py-1.5 rounded-full border border-purple-200 bg-purple-50 text-purple-700 hover:bg-purple-100 transition-colors"
                >
                  <Sparkles className="h-3 w-3 inline mr-1" />
                  {sugestao.label}
                </button>
              ))}
            </div>
          )}

          {isLoading
            && !messages.some(
              (m) => m.role === "assistant" && (m.status === "streaming" || m.status === "retrying" || m.status === "partial" || m.status === "error"),
            )
            // Suprime quando a última linha já é uma tool ("Buscando..."): esse
            // já é o indicador de progresso, não precisa dobrar com "Pensando...".
            && messages[messages.length - 1]?.role !== "tool" && (
            // Enquanto o modelo raciocina (antes do primeiro token) ou compõe a
            // resposta após os cards. O texto depois chega token a token.
            <div className="flex items-center gap-2 text-muted-foreground text-sm pl-2">
              <Loader2 className="h-4 w-4 animate-spin" />
              <span>Pensando...</span>
            </div>
          )}
        </div>
      </ScrollArea>

      {/* Input */}
      <div className="border-t p-3 space-y-2">
        {!isOnline && (
          <div className="flex items-center gap-1.5 rounded-md border border-amber-200 bg-amber-50 px-2 py-1.5 text-[11px] text-amber-800">
            <WifiOff className="h-3 w-3 shrink-0" />
            <span>Sem conexão. Verifique sua internet antes de enviar.</span>
          </div>
        )}
        <div className="flex gap-2">
          <Textarea
            ref={textareaRef}
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder="Ex: diretores de arte disponíveis daqui a 5 dias..."
            className="min-h-[60px] max-h-[120px] resize-none text-sm"
            disabled={isLoading}
          />
          {isLoading && onCancel ? (
            <Button
              onClick={onCancel}
              size="icon"
              variant="outline"
              className="shrink-0 self-end"
              aria-label="Parar resposta"
              title="Parar resposta"
            >
              <Square className="h-3.5 w-3.5 fill-current" />
            </Button>
          ) : (
            <Button
              onClick={handleSubmit}
              disabled={!input.trim() || isLoading}
              size="icon"
              className="shrink-0 self-end"
              aria-label="Enviar"
            >
              <Send className="h-4 w-4" />
            </Button>
          )}
        </div>
      </div>
    </div>
  );
}

const PARAM_LABELS: Record<string, string> = {
  areas: "Áreas",
  pretensao_max: "Pretensão máx.",
  pretensao_min: "Pretensão mín.",
  valor_maximo_estrito: "Teto de verba (filtro real)",
  areas_estritas: "Só estas áreas (filtro real)",
  excluir_ids: "Excluir IDs",
  series_agencias: "Portes agências",
  ordenar_por: "Ordenar por",
  limite: "Limite",
  query: "Busca",
  freelancer_id: "Freelancer",
  vaga_id: "Vaga",
  etapa: "Etapa",
  nota: "Nota",
  comentario: "Comentário",
  nome: "Nome",
  status: "Status",
};

function formatArgValue(value: unknown): string {
  if (Array.isArray(value)) return value.join(", ");
  if (typeof value === "boolean") return value ? "Sim" : "Não";
  if (value === null || value === undefined) return "-";
  return String(value);
}

function ToolArgs({ args }: { args: Record<string, unknown> }) {
  const entries = Object.entries(args).filter(
    ([, v]) => v !== null && v !== undefined
  );
  if (entries.length === 0) return null;

  return (
    <div className="mt-1 ml-5 space-y-0.5">
      {entries.map(([key, value]) => (
        <div key={key} className="flex gap-1.5 text-[11px] text-muted-foreground">
          <span className="font-medium shrink-0">{PARAM_LABELS[key] ?? key}:</span>
          <span className="break-all">{formatArgValue(value)}</span>
        </div>
      ))}
    </div>
  );
}

/**
 * Estabiliza markdown que ainda está sendo recebido por chunks SSE pra
 * não renderizar quebrado.
 *
 * O LLM pode abrir um code fence (```) ou tabela e ainda não ter mandado
 * o fechamento — o ReactMarkdown trata como código bagunçado e quebra a
 * UI visualmente até o próximo chunk completar. Solução: detectar
 * estruturas abertas e fechar TEMPORARIAMENTE pra render durante o
 * streaming. Quando `isComplete=true` (status === "complete"), devolve o
 * texto original sem modificação.
 */
function stabilizeStreamingMarkdown(text: string, isComplete: boolean): string {
  if (isComplete || !text) return text;
  let out = text;
  // Code fence aberto sem fechamento — adiciona ``` provisório.
  const fenceCount = (out.match(/```/g) || []).length;
  if (fenceCount % 2 === 1) out += "\n```";
  // Texto em itálico/bold com asterisco solto no fim — fecha provisório
  // pra não render como literal "* ".
  const lastLine = out.split("\n").pop() || "";
  const looseAsterisks = (lastLine.match(/\*+/g) || []).join("").length;
  if (looseAsterisks % 2 === 1) out += "*";
  return out;
}

/**
 * Status banner pra mensagem assistente quando a entrega quebrou (corte de
 * conexão durante streaming SSE, 5xx, etc).
 *
 * Princípio: NUNCA deixar a UI em branco. Sempre que algo der errado, deve
 * existir um indicador visual claro do estado (retentando / falhou / parcial)
 * e idealmente um caminho de recuperação manual.
 */
function StatusBanner({
  message,
  onRetry,
}: {
  message: AssistantMessage;
  onRetry?: () => void;
}) {
  if (message.role !== "assistant" || !message.status) return null;

  if (message.status === "retrying") {
    const sec = ((message.retryDelayMs ?? 0) / 1000).toFixed(1);
    return (
      <div className="mt-2 flex items-center gap-1.5 rounded-md border border-amber-200 bg-amber-50 px-2 py-1.5 text-[11px] text-amber-800">
        <WifiOff className="h-3 w-3 shrink-0 animate-pulse" />
        <span>
          Conexão perdida. Reconectando ({message.retryAttempt}/{message.retryMax})
          {Number(sec) > 0 ? <> em {sec}s…</> : "…"}
        </span>
      </div>
    );
  }

  if (message.status === "partial") {
    return (
      <div className="mt-2 flex items-center justify-between gap-2 rounded-md border border-amber-200 bg-amber-50 px-2 py-1.5 text-[11px] text-amber-800">
        <div className="flex items-center gap-1.5 min-w-0">
          <AlertTriangle className="h-3 w-3 shrink-0" />
          <span className="truncate">
            Resposta interrompida{message.errorDetail ? `: ${message.errorDetail}` : "."}
          </span>
        </div>
        {onRetry && (
          <Button
            size="sm"
            variant="outline"
            onClick={onRetry}
            className="h-6 px-2 text-[11px] gap-1 shrink-0"
          >
            <RefreshCw className="h-3 w-3" />
            Tentar de novo
          </Button>
        )}
      </div>
    );
  }

  if (message.status === "error") {
    return (
      <div className="mt-2 flex flex-col gap-1.5 rounded-md border border-red-200 bg-red-50 px-2 py-1.5 text-[11px] text-red-800">
        <div className="flex items-center gap-1.5">
          <AlertTriangle className="h-3 w-3 shrink-0" />
          <span className="font-medium">Não foi possível concluir</span>
        </div>
        {message.errorDetail && (
          <div className="ml-4 wrap-break-word text-red-700">{message.errorDetail}</div>
        )}
        {onRetry && (
          <div className="ml-4 mt-1">
            <Button
              size="sm"
              variant="outline"
              onClick={onRetry}
              className="h-6 px-2 text-[11px] gap-1"
            >
              <RefreshCw className="h-3 w-3" />
              Tentar novamente
            </Button>
          </div>
        )}
      </div>
    );
  }

  return null;
}

function MessageBubble({
  message,
  onRetry,
  onSelecionados,
  onShortlist,
  onDescartar,
  isProcessing,
}: {
  message: AssistantMessage;
  onRetry?: () => void;
  onSelecionados?: (freelancerId: string) => void;
  onShortlist?: (freelancerId: string) => void;
  onDescartar?: (freelancer: FreelancerSugerido) => void;
  isProcessing?: boolean;
}) {
  const [showCards, setShowCards] = useState(!message.cardsCollapsedDefault);
  if (message.role === "tool") {
    const hasArgs = message.toolArgs && Object.keys(message.toolArgs).length > 0;

    if (!hasArgs) {
      return (
        <div className="flex items-center gap-1.5 text-xs text-muted-foreground pl-2">
          <Wrench className="h-3 w-3" />
          <span>{message.content}</span>
        </div>
      );
    }

    return (
      <Collapsible className="pl-2">
        <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
          <Wrench className="h-3 w-3 shrink-0" />
          <span>{message.content}</span>
          <CollapsibleTrigger className="group p-0.5 rounded hover:bg-muted transition-colors">
            <ChevronRight className="h-3 w-3 transition-transform group-data-[state=open]:rotate-90" />
          </CollapsibleTrigger>
        </div>
        <CollapsibleContent>
          <ToolArgs args={message.toolArgs!} />
        </CollapsibleContent>
      </Collapsible>
    );
  }

  const isUser = message.role === "user";
  const isStreaming = message.role === "assistant" && message.status === "streaming";
  const isComplete = message.role !== "assistant" || message.status === "complete" || !message.status;
  const renderedContent = stabilizeStreamingMarkdown(message.content, isComplete);
  const hasCards = message.role === "assistant" && !!message.cards && message.cards.length > 0;
  const cardsCount = message.cardsMeta?.total_primary ?? message.cards?.length ?? 0;
  const hasText = message.content.trim().length > 0;
  // Bolha de texto só quando há conteúdo ou está streamando (mostra o pulse).
  // Mensagens só-de-cards (busca_match) não desenham bolha vazia.
  const showTextBubble = !isUser ? hasText || isStreaming : true;
  // Quando há StatusBanner, queremos ele logo abaixo da bubble — fora da
  // bubble mesmo, mas dentro da coluna de mensagem. Por isso a coluna vira
  // flex-col aqui pra empilhar bubble + banner.

  return (
    <div className={`flex gap-2 ${isUser ? "justify-end" : ""}`}>
      {!isUser && (
        <div className="shrink-0 mt-1">
          <div className="h-6 w-6 rounded-full bg-purple-100 flex items-center justify-center">
            <Bot className="h-3.5 w-3.5 text-purple-600" />
          </div>
        </div>
      )}

      <div className={`flex flex-col ${hasCards ? "w-full min-w-0" : "max-w-[85%]"}`}>
        {showTextBubble && (
          <div
            className={`rounded-lg px-3 py-2 text-sm ${
              isUser
                ? "bg-primary text-primary-foreground"
                : "bg-muted"
            }`}
          >
            <div className="prose prose-sm prose-neutral dark:prose-invert max-w-none wrap-break-word [&>p]:my-1 [&>ul]:my-1 [&>ol]:my-1 [&_li]:my-0.5 [&_a]:text-purple-600 [&_a]:underline [&>hr]:my-2 [&>hr]:border-border/50">
              <ReactMarkdown
                components={{
                  a: ({ href, children }) => (
                    <a href={href} target="_blank" rel="noopener noreferrer">{children}</a>
                  ),
                }}
              >
                {renderedContent}
              </ReactMarkdown>
              {/* Indicador visual de streaming ativo: 3 pontos pulsando ao final
                  do conteúdo. Substitui o spinner global de "Processando..." pelo
                  feedback local da mensagem que está chegando. */}
              {isStreaming && (
                <span
                  className="inline-flex items-center gap-0.5 ml-1 align-middle"
                  aria-label="Recebendo resposta"
                >
                  <span className="h-1 w-1 rounded-full bg-current opacity-60 animate-pulse" style={{ animationDelay: "0ms" }} />
                  <span className="h-1 w-1 rounded-full bg-current opacity-60 animate-pulse" style={{ animationDelay: "150ms" }} />
                  <span className="h-1 w-1 rounded-full bg-current opacity-60 animate-pulse" style={{ animationDelay: "300ms" }} />
                </span>
              )}
            </div>
            {message.freelancersMencionados && message.freelancersMencionados.length > 0 && (
              <Badge variant="secondary" className="mt-1 text-[10px]">
                {message.freelancersMencionados.length} freelancers
              </Badge>
            )}
          </div>
        )}

        {/* Cards ricos de freelancers renderizados inline (substitui a aba).
            Vêm como mensagem própria, com um cabeçalho clicável: ao reabrir a
            conversa a lista nasce recolhida e o clique no número expande. */}
        {hasCards && (
          <div className="mt-1 space-y-2">
            <button
              type="button"
              onClick={() => setShowCards((v) => !v)}
              className="inline-flex items-center gap-1 rounded-md border border-purple-200 bg-purple-50 px-2 py-1 text-[11px] font-medium text-purple-700 hover:bg-purple-100 transition-colors"
              aria-expanded={showCards}
            >
              <span>{cardsCount} freelancers</span>
              <ChevronRight className={`h-3 w-3 transition-transform ${showCards ? "rotate-90" : ""}`} />
            </button>

            {showCards && (
              <div className="space-y-2">
                {message.transparencia && message.transparencia.length > 0 && (
                  <div className="rounded-md border border-border/50 bg-muted/40 px-2.5 py-1.5 text-[11px] text-muted-foreground space-y-0.5">
                    <div className="flex items-center gap-1 font-medium text-foreground/70">
                      <Wrench className="h-3 w-3" />
                      {message.transparencia.some((t) => /^removi/i.test(t.efeito))
                        ? "Filtros aplicados (alguns retiraram freelancers da lista)"
                        : "Filtros aplicados como sinais (ninguém foi excluído)"}
                    </div>
                    {message.transparencia.map((t, i) => (
                      <div key={i} className="pl-4">
                        <span className="font-medium">{t.filtro}:</span> {t.efeito}
                      </div>
                    ))}
                  </div>
                )}
                <PaginatedFreelancerCards
                  key={message.serverId ?? message.id}
                  messageId={message.serverId}
                  initialPrimary={message.cards!}
                  initialExploratory={message.cardsExploratory}
                  cardsMeta={message.cardsMeta}
                  onSelecionados={onSelecionados}
                  onShortlist={onShortlist}
                  onDescartar={onDescartar}
                  isProcessing={isProcessing}
                />
              </div>
            )}
          </div>
        )}

        <StatusBanner message={message} onRetry={onRetry} />
      </div>

      {isUser && (
        <div className="shrink-0 mt-1">
          <div className="h-6 w-6 rounded-full bg-blue-100 flex items-center justify-center">
            <User className="h-3.5 w-3.5 text-blue-600" />
          </div>
        </div>
      )}
    </div>
  );
}
